import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Three separate, focused PDF builders — one per report tab (Windows, Doors,
// Cut Parts) — sharing the same brand chrome and drawing helpers. Each
// export produces its OWN document with only that tab's data, so exporting
// from the Doors tab never hands back the Windows report and vice versa.

// ─────────────────────────────────────────────────────────────────────────────
// PALETTE — same warm brown brand as the contract/estimate PDFs
// ─────────────────────────────────────────────────────────────────────────────
const BROWN      = [139, 69, 19];
const BROWN_DARK = [101, 48, 10];
const CREAM      = [252, 249, 245];
const HEAD_BG    = [242, 236, 228];
const HEAD_TEXT  = [70, 35, 8];
const GRAY_LINE  = [220, 215, 208];
const GRAY_TEXT  = [100, 95, 90];
const DARK       = [25, 22, 18];

const PAGE_W = 612, PAGE_H = 792, ML = 40, MR = 40, CW = PAGE_W - ML - MR;
const LOGO_URL = '/brand/twp-logo.png';

// Same fetch-and-cache-as-data-URL pattern used by contract-pdf.js, reused
// here so every one of these PDFs carries the same logo without a second
// brand asset.
let _logo = null;
async function getLogo() {
  if (_logo) return _logo;
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    return new Promise(resolve => {
      const fr = new FileReader();
      fr.onloadend = () => { _logo = fr.result; resolve(_logo); };
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

function ensureSpace(doc, y, needed) {
  if (y + needed > PAGE_H - 60) {
    doc.addPage();
    return 56;
  }
  return y;
}

function drawHeader(doc, logo, title, subtitle) {
  let y = 46;
  const textX = logo ? ML + 36 : ML;
  if (logo) { try { doc.addImage(logo, 'PNG', ML, y - 22, 28, 28); } catch { /* ignore bad image data */ } }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(...BROWN_DARK);
  doc.text(title, textX, y);
  y += 16;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(...GRAY_TEXT);
  doc.text(subtitle, textX, y);
  y += 14;
  doc.setDrawColor(...GRAY_LINE);
  doc.line(ML, y, PAGE_W - MR, y);
  return y + 22;
}

function sectionHeader(doc, y, text) {
  y = ensureSpace(doc, y, 40);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...BROWN_DARK);
  doc.text(text.toUpperCase(), ML, y);
  doc.setDrawColor(...BROWN); doc.setLineWidth(1.2);
  doc.line(ML, y + 5, ML + 26, y + 5);
  return y + 20;
}

function note(doc, y, text) {
  y = ensureSpace(doc, y, 16);
  doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.setTextColor(...GRAY_TEXT);
  doc.text(text, ML, y);
  return y + 16;
}

function statGrid(doc, y, stats) {
  const cols = 3;
  const colW = CW / cols;
  const rowH = 42;
  const rows = Math.ceil(stats.length / cols);
  y = ensureSpace(doc, y, rows * rowH + 8);
  stats.forEach((s, i) => {
    const cx = ML + (i % cols) * colW;
    const cy = y + Math.floor(i / cols) * rowH;
    doc.setFillColor(...CREAM);
    doc.roundedRect(cx, cy, colW - 10, rowH - 8, 4, 4, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...GRAY_TEXT);
    doc.text(s.label.toUpperCase(), cx + 8, cy + 13);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...BROWN_DARK);
    doc.text(String(s.value), cx + 8, cy + 29, { maxWidth: colW - 20 });
  });
  return y + rows * rowH + 8;
}

// Horizontal bar chart, one row per record, page-break aware so a long
// month-by-month trend doesn't run off the bottom of the page.
function barChart(doc, y, rows, { labelKey, valueKey = 'totalQty', maxBars = 10, valueSuffix = '' }) {
  const top = rows.slice(0, maxBars);
  if (top.length === 0) return y;
  const max = Math.max(1, ...top.map(r => r[valueKey]));
  const barH = 14, gap = 7, labelW = 150, barMaxW = CW - labelW - 55;

  top.forEach(r => {
    y = ensureSpace(doc, y, barH + gap);
    const label = String(r[labelKey] ?? '').slice(0, 30);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...DARK);
    doc.text(label, ML, y + barH * 0.72);
    const w = Math.max(2, (r[valueKey] / max) * barMaxW);
    doc.setFillColor(...BROWN);
    doc.rect(ML + labelW, y, w, barH, 'F');
    doc.setFontSize(8); doc.setTextColor(...GRAY_TEXT);
    doc.text(`${r[valueKey]}${valueSuffix}`, ML + labelW + w + 5, y + barH * 0.72);
    y += barH + gap;
  });

  return y + 8;
}

function dataTable(doc, y, head, body) {
  autoTable(doc, {
    startY: y,
    head: [head],
    body,
    styles: { fontSize: 7.5, cellPadding: 4.5, textColor: DARK, lineColor: GRAY_LINE, lineWidth: 0.4, overflow: 'linebreak' },
    headStyles: { fillColor: HEAD_BG, textColor: HEAD_TEXT, fontStyle: 'bold', fontSize: 7.5 },
    alternateRowStyles: { fillColor: [250, 247, 243] },
    margin: { left: ML, right: MR, bottom: 50 },
  });
  return doc.lastAutoTable.finalY + 18;
}

function drawFooters(doc) {
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...GRAY_TEXT);
    doc.text('© The Window Place LLC', ML, PAGE_H - 26);
    const pageText = `Page ${i} of ${totalPages}`;
    doc.text(pageText, PAGE_W - MR - doc.getTextWidth(pageText), PAGE_H - 26);
  }
}

const p = r => `${r.pctOfTotal.toFixed(1)}%`;
const generatedOn = () => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

// `windows` is the exact same summarized object the on-screen Windows tab
// renders — this PDF never recomputes anything, so the numbers always match
// what was reviewed on screen. Color is a distinct column throughout: a
// White 20x40 Double Hung and a Dk Woodgrain/White 20x40 Double Hung are
// different products, never blended into one "size" line.
export async function buildWindowsAnalyticsPDF({ presetLabel, typeFilterLabel, windows }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const logo = await getLogo();

  const scopeLabel = [presetLabel, typeFilterLabel].filter(Boolean).join(' · ');
  let y = drawHeader(doc, logo, 'Window Sales Report', `${scopeLabel} · Generated ${generatedOn()}`);

  y = sectionHeader(doc, y, 'Window Summary');
  y = statGrid(doc, y, [
    { label: 'Windows Ordered', value: windows.totalQty.toLocaleString() },
    { label: 'Jobs', value: windows.totalJobs },
    { label: 'Total Sq Ft', value: Math.round(windows.totalSqFt).toLocaleString() },
    { label: 'Most Ordered Type', value: windows.topType?.typeLabel || '—' },
    { label: 'Most Ordered Size', value: windows.topSize ? `${windows.topSize.size} (${windows.topSize.color})` : '—' },
    { label: 'Most Ordered Color', value: windows.topColor?.color || '—' },
  ]);

  if (windows.totalQty > 0) {
    y = sectionHeader(doc, y, 'Top Window Types');
    y = barChart(doc, y, windows.byType, { labelKey: 'typeLabel', maxBars: 8 });
    y = dataTable(doc, y, ['Type', 'Times Ordered', 'Total Qty', 'Jobs', '% of Total'],
      windows.byType.map(r => [r.typeLabel, r.timesOrdered, r.totalQty, r.jobCount, p(r)]));

    y = sectionHeader(doc, y, `Top Exact Sizes${windows.bySize.length > 40 ? ' (Top 40)' : ''}`);
    y = note(doc, y, 'Sizes are matched exactly, and color is part of the match — a size only appears once here per width, height, AND color combination.');
    y = dataTable(doc, y, ['Type', 'Size', 'Color', 'Configs', 'Times Ordered', 'Total Qty', '% of Total'],
      windows.bySize.slice(0, 40).map(r => [r.typeLabel, r.size, r.color, r.configs, r.timesOrdered, r.totalQty, p(r)]));
    if (windows.bySize.length > 40) {
      y = note(doc, y, `+ ${windows.bySize.length - 40} more distinct size/color combinations — see the CSV export for the full list.`);
    }

    y = sectionHeader(doc, y, 'By Configuration');
    y = dataTable(doc, y, ['Type', 'Configuration', 'Times Ordered', 'Total Qty', 'Jobs', '% of Total'],
      windows.byConfig.map(r => [r.typeLabel, r.config, r.timesOrdered, r.totalQty, r.jobCount, p(r)]));

    y = sectionHeader(doc, y, 'By Glass Package');
    y = dataTable(doc, y, ['Glass Type', 'Glass Config', 'Times Ordered', 'Total Qty', 'Jobs', '% of Total'],
      windows.byGlass.map(r => [r.glassType, r.glassConfig, r.timesOrdered, r.totalQty, r.jobCount, p(r)]));

    y = sectionHeader(doc, y, 'By Color');
    y = barChart(doc, y, windows.byColor, { labelKey: 'color', maxBars: 8 });
    y = dataTable(doc, y, ['Color', 'Times Ordered', 'Total Qty', '% of Total'],
      windows.byColor.map(r => [r.color, r.timesOrdered, r.totalQty, p(r)]));

    y = sectionHeader(doc, y, `Top Customers${windows.byCustomer.length > 20 ? ' (Top 20)' : ''}`);
    y = dataTable(doc, y, ['Customer', 'Jobs', 'Windows Ordered', '% of Total'],
      windows.byCustomer.slice(0, 20).map(r => [r.customerName, r.jobCount, r.totalQty, p(r)]));

    y = sectionHeader(doc, y, 'Monthly Trend');
    barChart(doc, y, windows.byMonth, { labelKey: 'label', valueSuffix: ' units', maxBars: windows.byMonth.length });
  } else {
    note(doc, y, 'No sold windows in this range.');
  }

  drawFooters(doc);
  return doc;
}

// `doors` is the exact same summarized object the on-screen Doors tab
// renders. Color is part of every grouping here too — a White Entry Door
// and a Clay Entry Door are different products, never blended.
export async function buildDoorsAnalyticsPDF({ presetLabel, doors }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const logo = await getLogo();

  let y = drawHeader(doc, logo, 'Door Sales Report', `${presetLabel} · Generated ${generatedOn()}`);
  y = note(doc, y, 'Doors are estimate line items, not a manufactured cutlist item — size is only tracked when it was actually entered on the order.');
  y = statGrid(doc, y, [
    { label: 'Doors Ordered', value: doors.totalQty.toLocaleString() },
    { label: 'Job Orders', value: doors.totalJobOrders },
    { label: 'With Recorded Size', value: doors.sizeCoverage.sized },
    { label: 'Without Recorded Size', value: doors.sizeCoverage.unsized },
    { label: 'Most Ordered Type', value: doors.byType[0] ? `${doors.byType[0].style} (${doors.byType[0].color})` : '—' },
  ]);

  if (doors.totalQty > 0) {
    y = sectionHeader(doc, y, 'By Door Type & Color');
    const chartRows = doors.byType.slice(0, 8).map(r => ({ ...r, chartLabel: `${r.style} — ${r.color}` }));
    y = barChart(doc, y, chartRows, { labelKey: 'chartLabel', maxBars: 8 });
    y = dataTable(doc, y, ['Door Type', 'Color', 'Times Ordered', 'Total Qty', 'Job Orders', '% of Total'],
      doors.byType.map(r => [r.style, r.color, r.timesOrdered, r.totalQty, r.jobCount, p(r)]));

    if (doors.bySize.length > 0) {
      y = sectionHeader(doc, y, 'By Exact Size (doors with a recorded size only)');
      y = dataTable(doc, y, ['Door Type', 'Size', 'Color', 'Times Ordered', 'Total Qty', '% of Total'],
        doors.bySize.map(r => [r.style, r.size, r.color, r.timesOrdered, r.totalQty, p(r)]));
    } else {
      y = sectionHeader(doc, y, 'By Exact Size');
      y = note(doc, y, 'No doors in this range have a recorded width/height.');
    }

    y = sectionHeader(doc, y, 'Monthly Trend');
    barChart(doc, y, doors.byMonth, { labelKey: 'label', valueSuffix: ' units', maxBars: doors.byMonth.length });
  } else {
    note(doc, y, 'No ordered doors in this range.');
  }

  drawFooters(doc);
  return doc;
}

// `cutParts` is the exact same summarized object the on-screen Cut Parts
// tab renders. Color is part of every grouping — a White 5150 frame header
// and a Clay 5150 frame header are different stock, never combined into one
// pre-cut shelf item.
export async function buildCutPartsAnalyticsPDF({ presetLabel, typeFilterLabel, sectionFilterLabel, cutParts }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const logo = await getLogo();

  const scopeLabel = [presetLabel, typeFilterLabel, sectionFilterLabel].filter(Boolean).join(' · ');
  let y = drawHeader(doc, logo, 'Shop Pre-Cut Guide', `${scopeLabel} · Generated ${generatedOn()}`);
  y = note(doc, y, 'Exact stock lengths and pane sizes cut most often — a pre-cut shelf built from this list covers the highest-volume parts first. Color is part of every match: White and Clay stock are always kept separate. Size is only tracked once a job\'s cut list has been generated.');
  y = statGrid(doc, y, [
    { label: 'Jobs With Cut Data', value: `${cutParts.coverage.jobsWithCalc} of ${cutParts.coverage.totalJobs}` },
    { label: 'Distinct Part/Color Combos', value: cutParts.byLinearParts.length },
    { label: 'Distinct Glass Sizes', value: cutParts.byGlassSizes.length },
    { label: 'Distinct Screen Sizes', value: cutParts.byScreenSizes.length },
  ]);

  if (cutParts.byLinearParts.length > 0) {
    y = sectionHeader(doc, y, 'Top Frame & Sash Parts to Pre-Cut');
    const chartRows = cutParts.byLinearParts.slice(0, 10).map(r => ({ ...r, chartLabel: `${r.typeLabel} ${r.partLabel} @ ${r.length}" (${r.color})` }));
    y = barChart(doc, y, chartRows, { labelKey: 'chartLabel', valueSuffix: ' pcs', maxBars: 10 });
    y = dataTable(doc, y, ['Type', 'Section', 'Part', 'Length', 'Color', 'Part #', 'Pieces Needed', '% of Total'],
      cutParts.byLinearParts.slice(0, 40).map(r => [r.typeLabel, r.sectionLabel, r.partLabel, `${r.length}"`, r.color, r.partNumbers, r.totalQty, p(r)]));
    if (cutParts.byLinearParts.length > 40) {
      y = note(doc, y, `+ ${cutParts.byLinearParts.length - 40} more part/length/color combinations — see the CSV export for the full list.`);
    }
  } else {
    y = note(doc, y, 'No cut parts found for this range.');
  }

  if (cutParts.byGlassSizes.length > 0) {
    y = sectionHeader(doc, y, 'Top Glass Sizes to Pre-Cut');
    y = dataTable(doc, y, ['Type', 'Pane', 'Size', 'Color', 'Times Ordered', 'Total Qty', '% of Total'],
      cutParts.byGlassSizes.slice(0, 30).map(r => [r.typeLabel, r.paneLabel, r.size, r.color, r.timesOrdered, r.totalQty, p(r)]));
  }

  if (cutParts.byScreenSizes.length > 0) {
    y = sectionHeader(doc, y, 'Top Screen Sizes to Pre-Cut');
    dataTable(doc, y, ['Type', 'Screen', 'Size', 'Color', 'Times Ordered', 'Total Qty', '% of Total'],
      cutParts.byScreenSizes.slice(0, 30).map(r => [r.typeLabel, r.screenLabel, r.size, r.color, r.timesOrdered, r.totalQty, p(r)]));
  }

  drawFooters(doc);
  return doc;
}
